import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Activity, Eye, MousePointerClick, UserPlus, Users, FileText, TrendingUp, ArrowRight, Link2, Mail, Copy, CheckCircle, Trash2, Send, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;

const EVENT_LABELS = {
  page_view: "Page Views",
  application_started: "Apply Modal Opens",
  application_submitted: "Applications Submitted",
  cta_click: "CTA Clicks",
  section_view: "Section Views",
  role_preview_viewed: "Welcome-Page Views",
  demo_entered: "Demo Sessions Started",
  demo_exited: "Demo Sessions Exited",
  tour_step_viewed: "Tour Steps Viewed",
};

function Stat({ icon: Icon, label, value, color = "text-white" }) {
  return (
    <div className="bg-[#09120D] border border-[#1A2E22] p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-[#A1B2A8] text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <p className={`font-['Outfit'] text-3xl font-bold ${color}`}>{value ?? 0}</p>
    </div>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [links, setLinks] = useState([]);
  const [emailQ, setEmailQ] = useState(null);
  const [newLink, setNewLink] = useState({ fund_name: "", investor_name: "" });
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState("");
  const [flushing, setFlushing] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [a, l, eq] = await Promise.all([
        axios.get(`${API}/admin/analytics`, { withCredentials: true }),
        axios.get(`${API}/admin/investor-links`, { withCredentials: true }),
        axios.get(`${API}/admin/email-queue`, { withCredentials: true }),
      ]);
      setData(a.data); setLinks(l.data); setEmailQ(eq.data);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { fetchAll(); const t = setInterval(fetchAll, 15000); return () => clearInterval(t); }, [fetchAll]);

  const createLink = async (e) => {
    e.preventDefault();
    if (!newLink.fund_name.trim()) return;
    setCreating(true);
    try {
      const { data: l } = await axios.post(`${API}/admin/investor-links`, newLink, { withCredentials: true });
      navigator.clipboard.writeText(l.url);
      setCopied(l.id);
      setTimeout(() => setCopied(""), 2000);
      setNewLink({ fund_name: "", investor_name: "" });
      fetchAll();
    } finally { setCreating(false); }
  };
  const deleteLink = async (id) => {
    if (!window.confirm("Delete this link?")) return;
    await axios.delete(`${API}/admin/investor-links/${id}`, { withCredentials: true });
    fetchAll();
  };
  const flushEmails = async () => {
    setFlushing(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/email-queue/flush`, {}, { withCredentials: true });
      alert(r.ok ? `Sent ${r.sent} of ${r.attempted} emails.` : `Provider not configured: ${r.reason}`);
      fetchAll();
    } finally { setFlushing(false); }
  };
  const copyUrl = (id, url) => { navigator.clipboard.writeText(url); setCopied(id); setTimeout(() => setCopied(""), 2000); };

  if (!data) return <div className="text-[#A1B2A8] text-sm">Loading analytics…</div>;
  const e = data.by_event || {};
  const r = data.by_role || {};
  const buyerSubmits = (r.buyer || {}).application_submitted || 0;
  const expSubmits = (r.exporter || {}).application_submitted || (r.supplier || {}).application_submitted || 0;
  const invSubmits = (r.investor || {}).application_submitted || 0;
  const submitTotal = buyerSubmits + expSubmits + invSubmits;
  const opens = e.application_started || 0;
  const conv = opens ? Math.round((submitTotal / opens) * 100) : 0;
  const baseUrl = window.location.origin;

  // Group campaigns by source/medium/campaign across events
  const campaignMap = {};
  (data.by_campaign || []).forEach((c) => {
    const k = `${c.utm_source || "?"}/${c.utm_medium || "?"}/${c.utm_campaign || "?"}`;
    campaignMap[k] = campaignMap[k] || { utm_source: c.utm_source, utm_medium: c.utm_medium, utm_campaign: c.utm_campaign, sessions: 0, applications: 0, page_views: 0, total: 0 };
    campaignMap[k].total += c.count;
    campaignMap[k].sessions = Math.max(campaignMap[k].sessions, c.sessions);
    if (c.event === "application_submitted") campaignMap[k].applications += c.count;
    if (c.event === "page_view") campaignMap[k].page_views += c.count;
  });
  const campaigns = Object.values(campaignMap).sort((a, b) => b.total - a.total);

  // Variant grouping
  const variantMap = {};
  (data.by_variant || []).forEach((v) => {
    variantMap[v.role] = variantMap[v.role] || {};
    variantMap[v.role][v.variant] = v.count;
  });

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-['Outfit'] text-2xl font-bold text-white">Live Analytics</h1>
      </div>
      <p className="text-[#A1B2A8]/60 text-sm mb-6">Last 30 days · auto-refresh every 15s</p>

      <h2 className="text-[#A1B2A8] text-xs font-semibold uppercase tracking-[0.2em] mb-3">Headline</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="analytics-headline">
        <Stat icon={Activity} label="Total Events" value={data.total_events} color="text-white" />
        <Stat icon={Users} label="Unique Sessions" value={data.unique_sessions} color="text-blue-400" />
        <Stat icon={Eye} label="Page Views" value={e.page_view} color="text-cyan-400" />
        <Stat icon={MousePointerClick} label="CTA Clicks" value={e.cta_click} color="text-purple-400" />
        <Stat icon={UserPlus} label="Apply Opens" value={opens} color="text-yellow-400" />
        <Stat icon={FileText} label="Applications" value={submitTotal} color="text-[#22C55E]" />
      </div>

      <h2 className="text-[#A1B2A8] text-xs font-semibold uppercase tracking-[0.2em] mb-3">Conversion Funnel</h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-8" data-testid="analytics-funnel">
        {[
          { label: "Sessions", value: data.unique_sessions, color: "text-white" },
          { label: "Apply Modal Opens", value: opens, color: "text-yellow-400" },
          { label: "Applications Submitted", value: submitTotal, color: "text-[#22C55E]" },
          { label: "Conversion", value: `${conv}%`, color: "text-[#22C55E]" },
        ].map((s, i, arr) => (
          <div key={i} className="bg-[#09120D] border border-[#1A2E22] p-5 relative">
            <p className="text-[#A1B2A8] text-[10px] uppercase tracking-wider mb-1">Step {i + 1}</p>
            <p className="text-white text-sm font-medium">{s.label}</p>
            <p className={`font-['Outfit'] text-2xl font-bold mt-2 ${s.color}`}>{s.value}</p>
            {i < arr.length - 1 && <ArrowRight className="hidden md:block w-4 h-4 text-[#1A2E22] absolute -right-2 top-1/2 -translate-y-1/2 z-10" />}
          </div>
        ))}
      </div>

      <h2 className="text-[#A1B2A8] text-xs font-semibold uppercase tracking-[0.2em] mb-3">By Persona</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8" data-testid="analytics-by-role">
        {[
          { role: "Buyer", color: "text-blue-400", border: "border-blue-400/30", bg: "bg-blue-400/5", data: r.buyer || {}, submits: buyerSubmits },
          { role: "Exporter", color: "text-purple-400", border: "border-purple-400/30", bg: "bg-purple-400/5", data: r.exporter || r.supplier || {}, submits: expSubmits },
          { role: "Investor", color: "text-[#22C55E]", border: "border-[#22C55E]/30", bg: "bg-[#22C55E]/5", data: r.investor || {}, submits: invSubmits },
        ].map((p) => (
          <div key={p.role} className={`border ${p.border} ${p.bg} p-5`} data-testid={`role-stats-${p.role.toLowerCase()}`}>
            <p className={`font-['Outfit'] text-lg font-bold ${p.color}`}>{p.role}</p>
            <div className="grid grid-cols-3 gap-2 mt-3">
              <div><p className="text-[#A1B2A8] text-[10px] uppercase">Opens</p><p className="text-white font-semibold">{p.data.application_started || 0}</p></div>
              <div><p className="text-[#A1B2A8] text-[10px] uppercase">Submits</p><p className={`font-semibold ${p.color}`}>{p.submits}</p></div>
              <div><p className="text-[#A1B2A8] text-[10px] uppercase">Previews</p><p className="text-white font-semibold">{p.data.role_preview_viewed || 0}</p></div>
            </div>
          </div>
        ))}
      </div>

      {/* A/B variants */}
      <h2 className="text-[#A1B2A8] text-xs font-semibold uppercase tracking-[0.2em] mb-3">A/B Variants — Welcome Pages</h2>
      <div className="bg-[#09120D] border border-[#1A2E22] mb-8" data-testid="analytics-by-variant">
        {Object.keys(variantMap).length === 0 ? (
          <p className="p-4 text-[#A1B2A8]/40 text-sm">No variant data yet — visit /welcome/{`{role}`}?v=a or ?v=b to seed.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-[#1A2E22]">
              <tr className="text-[#A1B2A8] text-[10px] uppercase tracking-wider">
                <th className="text-left p-3">Persona</th>
                <th className="text-right p-3">Variant A</th>
                <th className="text-right p-3">Variant B</th>
                <th className="text-right p-3">Winner</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(variantMap).map(([role, v]) => {
                const a = v.a || 0, b = v.b || 0;
                const winner = a === b ? "—" : (a > b ? "A" : "B");
                return (
                  <tr key={role} className="border-b border-[#1A2E22] last:border-0">
                    <td className="p-3 text-white capitalize">{role}</td>
                    <td className="p-3 text-right text-blue-400 font-semibold">{a}</td>
                    <td className="p-3 text-right text-purple-400 font-semibold">{b}</td>
                    <td className={`p-3 text-right font-bold ${winner === "A" ? "text-blue-400" : winner === "B" ? "text-purple-400" : "text-[#A1B2A8]/40"}`}>{winner}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* UTM Campaigns */}
      <h2 className="text-[#A1B2A8] text-xs font-semibold uppercase tracking-[0.2em] mb-3">Top Campaigns (UTM)</h2>
      <div className="bg-[#09120D] border border-[#1A2E22] mb-8" data-testid="analytics-by-campaign">
        {campaigns.length === 0 ? (
          <p className="p-4 text-[#A1B2A8]/40 text-sm">No UTM data yet — share links with ?utm_source=...&utm_medium=...&utm_campaign=...</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="border-b border-[#1A2E22]">
              <tr className="text-[#A1B2A8] text-[10px] uppercase tracking-wider">
                <th className="text-left p-3">Source / Medium / Campaign</th>
                <th className="text-right p-3">Sessions</th>
                <th className="text-right p-3">Page Views</th>
                <th className="text-right p-3">Applications</th>
                <th className="text-right p-3">Conv %</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.slice(0, 15).map((c, i) => {
                const cv = c.sessions ? Math.round((c.applications / c.sessions) * 100) : 0;
                return (
                  <tr key={i} className="border-b border-[#1A2E22] last:border-0">
                    <td className="p-3 text-white font-mono text-[11px]">{c.utm_source || "—"} / {c.utm_medium || "—"} / {c.utm_campaign || "—"}</td>
                    <td className="p-3 text-right text-white">{c.sessions}</td>
                    <td className="p-3 text-right text-cyan-400">{c.page_views}</td>
                    <td className="p-3 text-right text-[#22C55E] font-semibold">{c.applications}</td>
                    <td className="p-3 text-right text-[#22C55E]">{cv}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Per-Investor Tokenized Links */}
      <h2 className="text-[#A1B2A8] text-xs font-semibold uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
        <Link2 className="w-3.5 h-3.5 text-[#22C55E]" /> Per-Investor Tokenized Demo Links
      </h2>
      <div className="bg-[#09120D] border border-[#1A2E22] mb-8 p-4" data-testid="investor-links-panel">
        <form onSubmit={createLink} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4" data-testid="investor-link-form">
          <input value={newLink.fund_name} onChange={(ev) => setNewLink({ ...newLink, fund_name: ev.target.value })}
            placeholder="Fund name (e.g., Sequoia)" required
            className="bg-[#050A07] border border-[#1A2E22] text-white text-sm px-3 py-2 focus:border-[#22C55E] outline-none"
            data-testid="link-fund-name" />
          <input value={newLink.investor_name} onChange={(ev) => setNewLink({ ...newLink, investor_name: ev.target.value })}
            placeholder="Investor name (optional)"
            className="bg-[#050A07] border border-[#1A2E22] text-white text-sm px-3 py-2 focus:border-[#22C55E] outline-none"
            data-testid="link-investor-name" />
          <button type="submit" disabled={creating}
            className="bg-[#22C55E] text-[#050A07] hover:bg-[#1CAB4D] px-4 py-2 font-semibold text-sm disabled:opacity-50"
            data-testid="create-link-btn">
            {creating ? "Creating…" : "Generate & Copy URL"}
          </button>
        </form>
        {links.length === 0 ? (
          <p className="text-[#A1B2A8]/40 text-sm py-4">No links yet. Create one — copy will be auto-copied to clipboard.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="border-b border-[#1A2E22]">
              <tr className="text-[#A1B2A8] text-[10px] uppercase tracking-wider">
                <th className="text-left p-2">Fund</th>
                <th className="text-left p-2">Investor</th>
                <th className="text-right p-2">Sessions</th>
                <th className="text-right p-2">Demo Entries</th>
                <th className="text-right p-2">Events</th>
                <th className="text-right p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {links.map((l) => {
                const tk = process.env.REACT_APP_PUBLIC_DEMO_TOKEN || "dgx-investor-2026";
                const url = `${baseUrl}/investor-demo?token=${tk}&via=${l.id}`;
                return (
                  <tr key={l.id} className="border-b border-[#1A2E22] last:border-0" data-testid={`link-row-${l.id}`}>
                    <td className="p-2 text-white font-semibold">{l.fund_name}</td>
                    <td className="p-2 text-[#A1B2A8]">{l.investor_name || "—"}</td>
                    <td className="p-2 text-right text-blue-400 font-semibold">{l.sessions}</td>
                    <td className="p-2 text-right text-[#22C55E] font-semibold">{l.demo_entered}</td>
                    <td className="p-2 text-right text-white">{l.events}</td>
                    <td className="p-2 text-right">
                      <button onClick={() => copyUrl(l.id, url)} className="text-[#22C55E] hover:text-white inline-flex items-center gap-1 mr-3 text-[11px]" data-testid={`copy-link-${l.id}`}>
                        {copied === l.id ? <><CheckCircle className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy URL</>}
                      </button>
                      <button onClick={() => deleteLink(l.id)} className="text-red-400/70 hover:text-red-400" data-testid={`delete-link-${l.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Email Queue */}
      <h2 className="text-[#A1B2A8] text-xs font-semibold uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
        <Mail className="w-3.5 h-3.5 text-yellow-400" /> Email Queue · Approval Notifications
      </h2>
      <div className="bg-[#09120D] border border-[#1A2E22] mb-8" data-testid="email-queue-panel">
        <div className="px-4 py-3 border-b border-[#1A2E22] flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap text-xs">
            <span className={`flex items-center gap-1.5 px-2 py-0.5 border ${emailQ?.provider_configured ? "border-[#22C55E]/40 text-[#22C55E]" : "border-yellow-400/40 text-yellow-400"}`}>
              {emailQ?.provider_configured ? <><CheckCircle className="w-3 h-3" /> Provider: {emailQ.provider}</> : <><AlertCircle className="w-3 h-3" /> No provider configured (queue only)</>}
            </span>
            <span className="text-[#A1B2A8]">From: {emailQ?.from_name} &lt;{emailQ?.from}&gt;</span>
            <span className="text-yellow-400">Pending: {emailQ?.counts?.pending || 0}</span>
            <span className="text-[#22C55E]">Sent: {emailQ?.counts?.sent || 0}</span>
            <span className="text-red-400">Failed: {emailQ?.counts?.failed || 0}</span>
          </div>
          <button onClick={flushEmails} disabled={flushing || !emailQ?.provider_configured}
            className="bg-[#22C55E]/10 border border-[#22C55E]/40 text-[#22C55E] px-3 py-1.5 text-xs font-semibold hover:bg-[#22C55E]/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            data-testid="flush-email-queue">
            <Send className="w-3 h-3" /> {flushing ? "Sending…" : "Send Pending"}
          </button>
        </div>
        {!emailQ?.provider_configured && (
          <div className="px-4 py-3 text-xs text-[#A1B2A8] bg-yellow-400/5 border-b border-yellow-400/20">
            Set <code className="text-yellow-400">EMAIL_PROVIDER</code> (resend|sendgrid) and <code className="text-yellow-400">EMAIL_API_KEY</code> in <code className="text-yellow-400">/app/backend/.env</code> to send pending approval emails. Until then, emails are queued and visible here.
          </div>
        )}
        {(emailQ?.items || []).length === 0 ? (
          <p className="p-4 text-[#A1B2A8]/40 text-sm">No emails queued yet. Approve an application from /admin/applications to queue one.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="border-b border-[#1A2E22]">
              <tr className="text-[#A1B2A8] text-[10px] uppercase tracking-wider">
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">To</th>
                <th className="text-left p-3">Subject</th>
                <th className="text-left p-3">Kind</th>
                <th className="text-right p-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {(emailQ.items || []).slice(0, 25).map((it) => (
                <tr key={it.id} className="border-b border-[#1A2E22] last:border-0" data-testid={`email-${it.id}`}>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 border text-[10px] uppercase ${
                      it.status === "sent" ? "text-[#22C55E] border-[#22C55E]/30" :
                      it.status === "failed" ? "text-red-400 border-red-400/30" :
                      "text-yellow-400 border-yellow-400/30"
                    }`}>{it.status}</span>
                  </td>
                  <td className="p-3 text-white">{it.to}</td>
                  <td className="p-3 text-[#A1B2A8] truncate max-w-md">{it.subject}</td>
                  <td className="p-3 text-purple-400 font-mono">{it.kind}</td>
                  <td className="p-3 text-right text-[#A1B2A8]/60">{new Date(it.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <h2 className="text-[#A1B2A8] text-xs font-semibold uppercase tracking-[0.2em] mb-3">Top Pages (last 30d)</h2>
      <div className="bg-[#09120D] border border-[#1A2E22] mb-8" data-testid="analytics-top-pages">
        {(data.top_pages || []).length === 0 && <p className="p-4 text-[#A1B2A8]/40 text-sm">No page-view data yet.</p>}
        {(data.top_pages || []).map((p, i) => (
          <div key={i} className="px-4 py-2 flex items-center justify-between border-b border-[#1A2E22] last:border-0">
            <span className="text-[#E8F3EC] text-sm font-mono">{p.page}</span>
            <span className="text-[#22C55E] font-semibold text-sm">{p.count}</span>
          </div>
        ))}
      </div>

      <h2 className="text-[#A1B2A8] text-xs font-semibold uppercase tracking-[0.2em] mb-3">All Events (last 30d)</h2>
      <div className="bg-[#09120D] border border-[#1A2E22] p-4 mb-8" data-testid="analytics-by-event">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(EVENT_LABELS).map(([k, label]) => (
            <div key={k} className="flex items-center justify-between border-b border-[#1A2E22] py-2">
              <span className="text-[#A1B2A8] text-xs">{label}</span>
              <span className="text-white font-semibold text-sm">{e[k] || 0}</span>
            </div>
          ))}
        </div>
      </div>

      <h2 className="text-[#A1B2A8] text-xs font-semibold uppercase tracking-[0.2em] mb-3">Recent Activity</h2>
      <div className="bg-[#09120D] border border-[#1A2E22] max-h-96 overflow-y-auto" data-testid="analytics-recent">
        {(data.recent || []).length === 0 && <p className="p-4 text-[#A1B2A8]/40 text-sm">No events yet — visit the landing page or /investor-demo to seed data.</p>}
        {(data.recent || []).map((ev, i) => (
          <div key={i} className="px-4 py-2 border-b border-[#1A2E22] last:border-0 flex items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <TrendingUp className="w-3 h-3 text-[#22C55E] flex-shrink-0" />
              <span className="text-[#22C55E] font-mono w-44 truncate">{ev.event}</span>
              <span className="text-[#A1B2A8]/60 truncate flex-1">{ev.page}</span>
              {ev.metadata?.role && <span className="text-purple-400">[{ev.metadata.role}]</span>}
              {ev.metadata?.utm_source && <span className="text-cyan-400">utm:{ev.metadata.utm_source}</span>}
              {ev.metadata?.via && <span className="text-yellow-400">via:{ev.metadata.via}</span>}
              {ev.metadata?.variant && <span className="text-blue-400">v:{ev.metadata.variant}</span>}
            </div>
            <span className="text-[#A1B2A8]/40">{new Date(ev.ts).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

